import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import { formatVerificationCodeError } from 'utils/errors';
import { AuthCode } from '../AuthCode';
import { FormError, Label } from '../Typography';

const InputRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

interface IAuthCodeProps {
  allowedCharacters?: RegExp;
  characters?: number;
  errorMessages?: string[];
  inputLabel?: string;
  onChange: (res: string) => void;
  password?: boolean; // changes input type to password
}

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const ContainerClassName = 'authCode--container';
const InputClassName = 'authCode--input';

const AuthCodeStyles = css`
  .${ContainerClassName} {
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    width: auto;
  }

  .${InputClassName} {
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${toRem(44)};
    height: ${toRem(55)};
    margin-right: ${toRem(theme.space.xs)};
    border-radius: ${toRem(5.5)};
    border: ${toRem(1)} solid ${theme.color.neutral[200]};
    outline: none;
    background-color: ${theme.color.white};
    font-family: ${theme.font.family.euclid};
    font-weight: ${theme.font.weight.regular};
    font-size: ${toRem(theme.font.size['3xl'])};
    line-height: ${lineHeight(theme.font.size['3xl'])};
    text-align: center;
    color: ${theme.color.neutral[900]};

    @media (min-width: ${theme.breakpoint.xsmall}px) {
      width: ${toRem(49.5)};
      height: ${toRem(60.5)};
    }

    @media (min-width: ${theme.breakpoint.small}px) {
      width: ${toRem(55)};
      height: ${toRem(66)};
    }

    &:last-child {
      margin-right: 0;
    }

    &::placeholder {
      color: ${theme.color.neutral[400]};
    }

    &:hover {
      border-color: #dfdfe0;
      background-color: ${theme.color.white};
    }

    &:focus {
      border-color: #99a3c4;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

const StyledForm = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  ${AuthCodeStyles}
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${toRem(theme.space.xl)};
`;

const StyledFormError = styled(FormError)`
  margin-top: ${toRem(theme.space.sm)};
  margin-bottom: ${toRem(theme.space.sm)};
`;

const AuthCodeInput = ({ allowedCharacters = /^[0-9]*$/, characters, errorMessages, inputLabel = 'Verification Code', onChange, password }: IAuthCodeProps) => {
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (errorMessages && errorMessages.length > 0) {
      setShowError(true);
    }
  }, [errorMessages]);

  const handleOnChange = (event: any) => {
    onChange(event);
    setShowError(false);
  };

  return (
    <Layout data-cy="auth-code-input">
      <StyledLabel>{inputLabel}</StyledLabel>

      <StyledForm>
        <InputRow>
          <AuthCode allowedCharacters={allowedCharacters} characters={characters} containerClassName={ContainerClassName} inputClassName={InputClassName} onChange={handleOnChange} password={password} />
        </InputRow>

        {errorMessages &&
          showError &&
          errorMessages.map((error, index) => (
            <StyledFormError data-cy="form-error" key={`error-message-${index}`}>
              {formatVerificationCodeError(error)}
            </StyledFormError>
          ))}
      </StyledForm>
    </Layout>
  );
};

export default AuthCodeInput;
